package at.theatervogelweide.admin.contract

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/** The image folders shared with the public site, see [directoryName]. */
@Serializable
enum class ImageCategory(val directoryName: String) {
    @SerialName("acts")
    ACTS("act-images"),

    @SerialName("news-items")
    NEWS_ITEMS("news-item-images"),

    @SerialName("persons")
    PERSONS("person-images"),

    /** Sponsor logos (#93), the only category that also takes SVG; the server sanitizes it (#92). */
    @SerialName("sponsors")
    SPONSORS("sponsor-images"),
}

/**
 * What an image says and where crops keep it (#105): [description] is the alt text, [credit] the photographer, both
 * blank for none; [focalX] and [focalY] are the focal point in percent from the left and the top, the centre by
 * default. [version] is the saved details' version, `null` while there are none: a save of changed details with
 * another version than the saved one is a conflict, so an editor never overwrites a change made meanwhile (e.g. by
 * MCP). Records carry them for their images in `imageDetails`, keyed by the image's id: an image without an entry
 * keeps its saved details, an entry with the content of [NONE] clears them.
 */
@Serializable
data class ImageDetails(
    val description: String = "",
    val credit: String = "",
    val focalX: Int = CENTRE,
    val focalY: Int = CENTRE,
    val version: Long? = null,
) {
    val hasDescription: Boolean get() = description.isNotBlank()

    /** Whether these say the same as [other], whatever their versions. */
    fun sameContentAs(other: ImageDetails): Boolean = copy(version = null) == other.copy(version = null)

    /** Whether there is anything to save: a description, a credit or a focal point off the centre. */
    val hasContent: Boolean get() = !sameContentAs(NONE)

    /** The rules for the details of [field]'s images, e.g. `images`. */
    fun validate(field: String): List<FieldError> = validate {
        field(field, description.trim()) { maxLength(MAX_DESCRIPTION_LENGTH) }
        field(field, credit.trim()) { maxLength(MAX_CREDIT_LENGTH) }
        check(field, focalX in 0..100 && focalY in 0..100) { "Der wichtigste Bildteil ist ungültig. Bitte neu laden." }
    }

    companion object {
        const val CENTRE = 50
        const val MAX_DESCRIPTION_LENGTH = 500
        const val MAX_CREDIT_LENGTH = 255

        val NONE = ImageDetails()
    }
}

/** The details of [uuids], each [ImageDetails.NONE] unless this map has others (as saved, i.e. without a version). */
fun Map<String, ImageDetails>.of(uuids: Collection<String>): Map<String, ImageDetails> =
    uuids.associateWith { this[it] ?: ImageDetails.NONE }

@Serializable
data class UploadedImage(
    val uuid: String,
    val fileName: String,
)

/** The largest image the server accepts; the web app checks it before uploading. */
const val MAX_IMAGE_MEGABYTES = 20
const val MAX_IMAGE_BYTES = MAX_IMAGE_MEGABYTES * 1024L * 1024

/** The image formats the public site shows; SVG is left out on purpose, it can carry scripts. */
val IMAGE_EXTENSIONS = setOf("jpg", "jpeg", "png", "gif", "webp")

const val SVG_EXTENSION = "svg"

/** What an upload of this category may be: [IMAGE_EXTENSIONS], and for sponsor logos SVG as well (#92). */
val ImageCategory.extensions: Set<String>
    get() = if (this == ImageCategory.SPONSORS) IMAGE_EXTENSIONS + SVG_EXTENSION else IMAGE_EXTENSIONS

/** The refusal of a file called [fileName] that isn't one of this category's [extensions]. */
fun ImageCategory.formatMessage(fileName: String): String =
    if (this == ImageCategory.SPONSORS) {
        "„$fileName“ ist kein Logo im Format SVG, PNG, JPEG, GIF oder WebP."
    } else {
        "„$fileName“ ist kein Bild im Format JPEG, PNG, GIF oder WebP."
    }

private val UUID = Regex("^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$")

/** Image ids are lower-case UUIDs; anything else never reaches the file system. */
fun isImageUuid(value: String): Boolean = UUID.matches(value)
